import { AgentPortalFeatureCode } from '@wallet-manager/pfh-pmp-node-def-types';
import { EnumCreditAllocationStatus } from '@wallet-manager/pfh-pmp-node-def-types/dist/src/DbModel/SwapAgent';
import { useState } from 'react';
import APIs from 'src/api';
import ApproveRejectCell from 'src/common/approval/components/ApproveRejectCell';
import ApproveRejectDialog from 'src/common/approval/components/ApproveRejectDialog';
import { usePageConfigProviderContext } from 'src/common/page/PageConfigProvider';
import { Params } from 'src/common/types/GridRenderCellParams';
import { useAppSelector } from 'src/reducer/hooks';
import { selectProfile } from 'src/reducer/profileSlice';
import { useZusTranslatePrefixStore } from 'src/zustand/store';
import { GetListRes } from '../types/GetListRes';
import { Container, Row, RowContent, RowHeader } from 'src/common/dialogs/layout/contentLayout';
import { useTranslation } from 'src/hooks';
import { displayAmountCurrying } from 'src/utils';

const useOperationsCell = () => {
  const { translatePrefix, onFilterSearch } = usePageConfigProviderContext();
  const { t, tc } = useTranslation('creditAllocation');
  const [selectedRawData, setSelectedRawData] = useState<GetListRes | null>(null);
  const [isApproveRejectDialogOpen, setIsApproveRejectDialogOpen] = useState<boolean>(false);

  const profile = useAppSelector(selectProfile);
  const { setTranslatePrefix } = useZusTranslatePrefixStore();

  const renderOperationsCell = ({ row }: Params<GetListRes>) => {
    const isDisabled = row.rawData.status !== EnumCreditAllocationStatus.Pending;

    const handleApproveRejectCellClick = async () => {
      setSelectedRawData(row.rawData);
      setIsApproveRejectDialogOpen(true);
      setTranslatePrefix('creditAllocation');
    };

    return <ApproveRejectCell onClick={handleApproveRejectCellClick} isDisabled={isDisabled} />;
  };

  const renderApproveRejectDialog = () => {
    const toDisplayAmount = displayAmountCurrying(selectedRawData?.creditCurrencyDecimals ?? 0);
    const creditAmountDisplayValue = toDisplayAmount(selectedRawData?.creditAmount ?? 0);
    return (
      <ApproveRejectDialog
        isOpen={isApproveRejectDialogOpen}
        closeDialog={() => {
          setIsApproveRejectDialogOpen(false);
          setTranslatePrefix(translatePrefix);
        }}
        refreshTable={onFilterSearch}
        data={{
          operator: profile.email,
          createdBy: selectedRawData?.createdBy || '',
          approvalsRequired: selectedRawData?.approvalsRequired ?? 0,
          approvalsCount: selectedRawData?.approvalsCount ?? 0,
          id: selectedRawData?.id?.toString() ?? '',
        }}
        actions={{
          approve: {
            apiFunc: APIs.distributorAgentManagement.creditAllocation.approve,
            permission:
              AgentPortalFeatureCode.DistributorAgentManagement.CreditAllocation.Approve.prefix,
          },
          reject: {
            apiFunc: APIs.distributorAgentManagement.creditAllocation.reject,
            permission: AgentPortalFeatureCode.DistributorAgentManagement.CreditAllocation.Reject,
          },
        }}
        getApproveHistory={APIs.distributorAgentManagement.creditAllocation.getApproveHistory}
      >
        <Container sx={{ padding: '40px' }}>
          <Row>
            <RowHeader>{tc('pfhNumber')}</RowHeader>
            <RowContent>{selectedRawData?.toWalletClientId}</RowContent>
          </Row>
          <Row>
            <RowHeader>{t('currency')}</RowHeader>
            <RowContent>{selectedRawData?.creditCurrency}</RowContent>
          </Row>
          <Row>
            <RowHeader>{t('transactionAmount')}</RowHeader>
            <RowContent>{creditAmountDisplayValue}</RowContent>
          </Row>
        </Container>
      </ApproveRejectDialog>
    );
  };

  return { renderOperationsCell, renderApproveRejectDialog };
};

export default useOperationsCell;
